import React from 'react';
import { Text, View } from '@react-pdf/renderer';

/**
 * Renders a proposal clause body that comes from the database.
 *
 * Clause text is stored as HTML in property_proposal_clause_types.default_text (or
 * overridden per proposal in property_proposal_clauses) and is returned by the API
 * with its {{placeholders}} already resolved against the proposal - so a clause such
 * as "{{council_tax}} will be responsible for the payment of Council Tax" arrives as
 * "Owner will be responsible ..." and stays in step with the liability dropdowns.
 *
 * The `fallback` paragraphs are only used when the clause is missing from the
 * database, so a proposal never renders with an empty section.
 */

const ENTITIES = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&#39;': "'",
  '&pound;': '£',
  '&bull;': '•',
  '&rsquo;': '’',
  '&lsquo;': '‘',
  '&ldquo;': '“',
  '&rdquo;': '”',
  '&ndash;': '–',
  '&mdash;': '—',
  '&hellip;': '…',
};

const decodeEntities = (text) =>
  Object.entries(ENTITIES)
    .reduce((acc, [entity, char]) => acc.split(entity).join(char), text)
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));

const stripTags = (html) =>
  decodeEntities(html.replace(/<[^>]*>/g, ''))
    .replace(/[ \t ]+/g, ' ')
    .trim();

const BLOCK_TAGS = /<(p|li|h[1-6]|div)\b[^>]*>([\s\S]*?)<\/\1>/gi;

/**
 * Turn a clause's HTML into flat blocks react-pdf can lay out.
 * Returns [{ type: 'paragraph' | 'listItem', text }].
 */
export const htmlToBlocks = (html) => {
  if (!html || typeof html !== 'string') return [];

  const normalised = html.replace(/<br\s*\/?>/gi, '\n');
  const blocks = [];

  const pushBlock = (type, rawText) => {
    stripTags(rawText)
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((text) => blocks.push({ type, text }));
  };

  let match;
  BLOCK_TAGS.lastIndex = 0;
  while ((match = BLOCK_TAGS.exec(normalised)) !== null) {
    pushBlock(match[1].toLowerCase() === 'li' ? 'listItem' : 'paragraph', match[2]);
  }

  // Clause bodies without any block markup (plain text or inline tags only)
  if (!blocks.length) pushBlock('paragraph', normalised);

  return blocks;
};

const ClauseBody = ({ html, fallback = [], styles = {} }) => {
  const blocks = htmlToBlocks(html);

  const paragraphs = blocks.length
    ? blocks
    : fallback.filter(Boolean).map((text) => ({ type: 'paragraph', text }));

  return (
    <>
      {paragraphs.map((block, index) =>
        block.type === 'listItem' ? (
          <View key={index} style={styles.bulletItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.text}>{block.text}</Text>
          </View>
        ) : (
          <Text key={index} style={styles.text}>
            {block.text}
          </Text>
        )
      )}
    </>
  );
};

export default ClauseBody;
